import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, Heading, Input, Label, Paragraph } from '@digdir/designsystemet-react';
import { FilesIcon } from '@navikt/aksel-icons';
import classes from '../WorkEnvironmentSurveyProcess.module.css';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import { Sharing, ShareStatus } from './ShareStatus';

const COPIED_MS = 2000;

type Props = {
  link: string;
  /** What the team does through the link. The survey itself by default. */
  text?: string;
  linkLabel?: string;
  /** The team's channels, and sending the link to them */
  sharing?: Sharing;
};

/** Only those who share with the team see the card; the safety representative leaves it to the team leader */
export const ShareCard = ({ link, text, linkLabel, sharing }: Props): ReactElement | null => {
  const { t } = useTranslation();
  const { may } = useSurveyActions();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = () => {
    navigator.clipboard
      .writeText(link)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  if (!may('share')) return null;

  return (
    <section aria-labelledby="share-heading" className={`${classes.card} ${classes.sideCard}`}>
      <div className={classes.sideHeading}>
        <Heading level={2} data-size="xs" id="share-heading">
          {t('workEnvironmentProcess.share.title')}
        </Heading>
        <Paragraph data-size="sm" className={classes.subtle}>
          {text ?? t('workEnvironmentProcess.share.text')}
        </Paragraph>
      </div>
      <Field data-size="sm">
        <Label htmlFor="survey-link">{linkLabel ?? t('workEnvironmentProcess.share.link')}</Label>
        <div className={classes.linkRow}>
          <Input id="survey-link" readOnly value={link} onFocus={(event) => event.target.select()} />
          <Button variant="secondary" data-size="sm" className={classes.noWrap} onClick={copy}>
            <FilesIcon aria-hidden />
            {copied ? t('workEnvironmentProcess.share.copied') : t('workEnvironmentProcess.share.copy')}
          </Button>
        </div>
      </Field>
      {sharing && <ShareStatus {...sharing} />}
    </section>
  );
};
